import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await register(name.trim(), email.trim(), password);
      toast.success("Account created");
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f5] grain flex flex-col">
      <header className="px-6 sm:px-10 py-5 border-b border-[#2a2a2a]">
        <Link to="/" className="flex items-center gap-2 w-fit">
          <div className="w-8 h-8 flex flex-col overflow-hidden">
            <div className="flex-1 bg-[#00209F]" />
            <div className="flex-1 bg-[#D21034]" />
          </div>
          <span className="font-display font-semibold tracking-tight">Rasin · Ayiti 🇭🇹</span>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#ff4500] font-mono">── Create account</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-4 tracking-tighter">Vin sou Rasin.</h1>
          <p className="text-[#a3a3a3] mt-3 text-sm">Free forever. Upload videos, host music, message creators, share Haiti.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="register-form">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Display name</label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name"
                className="mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] focus:ring-1 focus:ring-[#ff4500] outline-none px-4 py-3 text-sm"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email"
                className="mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] focus:ring-1 focus:ring-[#ff4500] outline-none px-4 py-3 text-sm"
                placeholder="you@domain.com"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password"
                className="mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] focus:ring-1 focus:ring-[#ff4500] outline-none px-4 py-3 text-sm"
                placeholder="At least 6 characters"
              />
            </div>

            {error && (
              <div data-testid="register-error" className="text-xs text-[#ff0033] border border-[#ff0033]/40 bg-[#ff0033]/10 px-3 py-2">{error}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit"
              className="w-full px-6 py-3 bg-[#ff4500] hover:bg-[#ff5722] disabled:opacity-50 text-white text-sm font-medium transition-colors active:scale-[0.98]"
            >
              {loading ? "Creating…" : "Create account"}
            </button>
          </form>

          <div className="mt-6 text-sm text-[#a3a3a3]">
            Have an account?{" "}
            <Link to="/login" className="text-[#ff4500] hover:underline" data-testid="goto-login">Sign in</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
